import { Box, Grid, Typography, TextField, Button, Skeleton } from '@mui/material';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings } from './i18n';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { BargeSelectModal } from './BargeSelectModal';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { LOCAL_STORAGE_KEYS } from '../../constants/localStorage';
import api from '../../config/axios';

interface GeneralSettingsProps {
  onSave?: (page: string, data: any) => void;
}

interface Barge {
  name: string;
  systemId: string;
}

export const GeneralSettings = ({ onSave }: GeneralSettingsProps) => {
  const { t } = useTranslation(Settings);
  const [isBargeSelectOpen, setIsBargeSelectOpen] = useState(false);
  const [selectedBarge, setSelectedBarge] = useLocalStorage<Barge | null>(
    LOCAL_STORAGE_KEYS.SELECTED_BARGE,
    null,
  );
  const [formData, setFormData] = useState({
    bargeName: selectedBarge?.name || '',
    bargeIMO: '',
    regionalIdentificationNumber: '',
    maritimeMobileServiceIdentity: '',
    edgeCredentials: '',
    webAppVersion: '',
  });
  const [loading, setLoading] = useState(true);

  const fetchBargeData = async (bargeId: string) => {
    try {
      setLoading(true);
      const { data } = await api.get(`/data-logger/mfm/settings/barge/${bargeId}`);

      setFormData((prev) => ({
        ...prev,
        bargeName: data.name,
        bargeIMO: data.imo,
        regionalIdentificationNumber: data.regionalIdentificationNumber,
        maritimeMobileServiceIdentity: data.maritimeMobileServiceIdentity,
      }));
    } catch (err) {
      console.error('Error fetching barge info:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedBarge?.systemId) {
      fetchBargeData(selectedBarge.systemId);
    } else {
      setIsBargeSelectOpen(true);
    }
  }, [selectedBarge?.systemId]);

  const handleBargeSelect = async (barge: Barge) => {
    setSelectedBarge(barge);
    setFormData((prev) => ({
      ...prev,
      bargeName: barge.name,
      bargeIMO: '',
      bargeSB: '',
      bargeMMSI: '',
    }));

    await fetchBargeData(barge.systemId);
  };

  const handleInputChange =
    (field: keyof typeof formData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleSubmit = () => {
    updateBargeSettings();
  };

  const updateBargeSettings = async () => {
    try {
      const { data } = await api.put(`/data-logger/mfm/settings/barge/${selectedBarge?.systemId}`, {
        BargeName: formData.bargeName,
        Imo: formData.bargeIMO,
        RegionalIdentificationNumber: formData.regionalIdentificationNumber,
        MaritimeMobileServiceIdentity: formData.maritimeMobileServiceIdentity,
      });

      console.log('Barge settings updated successfully:', data);
    } catch (err) {
      console.error('Error updating barge settings:', err);
    }
  };

  const isFormValid = () => {
    const imoValid = formData.bargeIMO === '' || /^IMO \d{7}$/.test(formData.bargeIMO);
    const mmsiValid =
      formData.maritimeMobileServiceIdentity === '' ||
      /^\d{9}$/.test(formData.maritimeMobileServiceIdentity);
    const regionalIdValid =
      formData.regionalIdentificationNumber === '' ||
      /^\d{8}$/.test(formData.regionalIdentificationNumber) ||
      /^SB [0-9A-Z]{3,20}$/.test(formData.regionalIdentificationNumber);

    return imoValid && mmsiValid && regionalIdValid;
  };

  const renderField = (label: string, field: keyof typeof formData) => (
    <>
      {loading ? (
        <Skeleton variant="text" width={100} height={20} sx={{ mb: 1 }} />
      ) : (
        <Typography variant="body2" gutterBottom color="textSecondary">
          {label}
        </Typography>
      )}
      {loading ? (
        <Skeleton variant="rounded" height={36} />
      ) : (
        <TextField
          fullWidth
          size="small"
          value={formData[field]}
          onChange={handleInputChange(field)}
          variant="outlined"
          error={
            (field === 'bargeIMO' &&
              formData.bargeIMO !== '' &&
              !/^IMO \d{7}$/.test(formData.bargeIMO)) ||
            (field === 'maritimeMobileServiceIdentity' &&
              formData.maritimeMobileServiceIdentity !== '' &&
              !/^\d{9}$/.test(formData.maritimeMobileServiceIdentity)) ||
            (field === 'regionalIdentificationNumber' &&
              formData.regionalIdentificationNumber !== '' &&
              !(
                /^\d{8}$/.test(formData.regionalIdentificationNumber) ||
                /^SB [0-9A-Z]{3,20}$/.test(formData.regionalIdentificationNumber)
              ))
          }
          helperText={
            field === 'bargeIMO' &&
            formData.bargeIMO !== '' &&
            !/^IMO \d{7}$/.test(formData.bargeIMO)
              ? 'IMO number must be in format "IMO XXXXXXX"'
              : field === 'maritimeMobileServiceIdentity' &&
                  formData.maritimeMobileServiceIdentity !== '' &&
                  !/^\d{9}$/.test(formData.maritimeMobileServiceIdentity)
                ? 'MMSI must be 9 digits'
                : field === 'regionalIdentificationNumber' &&
                    formData.regionalIdentificationNumber !== '' &&
                    !(
                      /^\d{8}$/.test(formData.regionalIdentificationNumber) ||
                      /^SB [0-9A-Z]{3,20}$/.test(formData.regionalIdentificationNumber)
                    )
                  ? 'Regional ID must be either 8 digits or in format "SB XXXXX"'
                  : ''
          }
        />
      )}
    </>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
        pb: '55px',
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
        }}
      >
        {/* General information section */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', mb: 2 }}>
          <Typography
            variant="h5"
            sx={{
              fontSize: '24px',
            }}
          >
            {t('generalSettings.settings')}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setIsBargeSelectOpen(true)}
            endIcon={<KeyboardArrowDownIcon />}
            sx={{
              color: (theme) => (theme.palette.mode === 'dark' ? '#fff' : '#000'),
              backgroundColor: (theme) => theme.palette.primary.selected,
              textTransform: 'capitalize',
              marginLeft: '16px',
              '&:hover': {
                borderColor: (theme) => theme.palette.primary.main,
                backgroundColor: (theme) => theme.palette.primary.selected,
              },
            }}
          >
            {t('generalSettings.selectedBarge', { barge: selectedBarge?.name })}
          </Button>
        </Box>

        <BargeSelectModal
          open={isBargeSelectOpen}
          onClose={() => setIsBargeSelectOpen(false)}
          onSelect={handleBargeSelect}
        />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography sx={{ marginTop: '32px' }} variant="h4" gutterBottom>
              {t('generalSettings.generalInformation')}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            {renderField(t('generalSettings.bargeName'), 'bargeName')}
          </Grid>
          <Grid item xs={6}>
            {renderField(t('generalSettings.bargeIMO'), 'bargeIMO')}
          </Grid>
          <Grid item xs={6}>
            {renderField(t('generalSettings.bargeRegionalID'), 'regionalIdentificationNumber')}
          </Grid>
          <Grid item xs={6}>
            {renderField(t('generalSettings.bargeMMSI'), 'maritimeMobileServiceIdentity')}
          </Grid>
        </Grid>

        {/* System configuration section
        <Grid item xs={12}>
          <Typography sx={{ marginTop: '32px' }} variant="h4" gutterBottom>
            {t('generalSettings.systemConfiguration')}
          </Typography>
        </Grid>
        <Alert
          severity="warning"
          icon={<WarningRoundedIcon sx={{ padding: '0px', color: '#F7A308' }} fontSize="small" />}
          sx={{
            mt: '16px',
            mb: '16px',
            padding: '2px 14px',
          }}
        >
          <Typography variant="body1" gutterBottom color="textPrimary" fontWeight="500">
            {t('generalSettings.adminWarning')}
          </Typography>
        </Alert>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Typography variant="body2" gutterBottom color="textSecondary">
              {t('generalSettings.edgeCredentials')}
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={formData.edgeCredentials}
              onChange={handleInputChange('edgeCredentials')}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" gutterBottom color="textSecondary">
              {t('generalSettings.webAppVersion')}
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={formData.webAppVersion}
              onChange={handleInputChange('webAppVersion')}
              variant="outlined"
              helperText={t('generalSettings.currentVersion', { version: formData.webAppVersion })}
            />
          </Grid>
        </Grid> */}
      </Box>

      {/* Fixed bottom button */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          p: 2,
          display: 'flex',
          justifyContent: 'flex-end',
          zIndex: 1000,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          sx={{ minWidth: '120px' }}
          disabled={loading || !isFormValid()}
        >
          {t('generalSettings.done')}
        </Button>
      </Box>
    </Box>
  );
};
